import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { MedicineApi } from '../../../api';
import type { Medicine } from '../../../model';
import { formatMoney } from '../../../utils';
import { Modal } from '../../molecules';

const CHARACTERS = 'АБВГДЕЁЖЗИЙКЛМНОПРСТУФХЦЧШЩЭЮЯ';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onMedicineSelect: (medicine: Medicine) => void;
};

export const SelectMedicineModal = ({
  isOpen,
  onClose,
  onMedicineSelect,
}: Props) => {
  const [searchText, setSearchText] = useState('');
  const [activeCharacter, setActiveCharacter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  const { data: medicines } = useQuery({
    queryFn: () =>
      MedicineApi.getAll(searchText, activeCharacter, currentPage, pageSize),
    queryKey: [
      MedicineApi.KEY,
      'getAll',
      searchText,
      activeCharacter,
      currentPage,
      pageSize,
    ],
    enabled: isOpen,
    keepPreviousData: true,
  });

  const medicineList = medicines?.items || [];
  const totalMedicines = medicines?.total || 0;
  const totalPages = Math.ceil(totalMedicines / pageSize);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleResetCharacter = useCallback(() => {
    setActiveCharacter('');
    setCurrentPage(1);
  }, []);

  const handleMedicineSelect = useCallback(
    (medicine: Medicine) => {
      onMedicineSelect(medicine);
      onClose();
    },
    [onClose, onMedicineSelect],
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="rounded-[8px] bg-[#F1F7FA]">
        <header className="shrink-0 px-[24px] pb-[24px] pt-[48px]">
          <input
            className="flex h-14 w-full items-center gap-[12px] rounded-[12px] border border-[#afc4d1] px-[20px] py-[18px] outline-none transition focus-within:border-[1.6px] focus-within:border-[#0788f1] hover:border-[#0788f1]"
            onChange={(event) => {
              setSearchText(event.target.value);
              setCurrentPage(1);
            }}
            placeholder="Аспирин, Байер, грипп..."
            type="text"
            value={searchText}
          />
        </header>
        <div className="rounded-[8px] bg-white">
          <span className="block shrink-0 px-[24px] pt-[24px] text-[12px] text-[#151515]/50">
            поиск препаратов по алфавиту
          </span>
          <div className="mt-[8px] flex shrink-0 flex-wrap items-center justify-between gap-y-[12px] px-[24px] pb-[24px]">
            <div className="flex flex-wrap gap-[8px]">
              {CHARACTERS.split('').map((character) => (
                <button
                  key={character}
                  type="button"
                  className={`border-b text-[18px] leading-none transition-all hover:text-[#151515] ${
                    activeCharacter === character
                      ? 'border-[#151515]'
                      : 'border-transparent'
                  }`}
                  onClick={() => {
                    setActiveCharacter((prev) =>
                      prev === character ? '' : character,
                    );
                    setCurrentPage(1);
                  }}
                >
                  {character}
                </button>
              ))}
            </div>
            <button
              className={`rounded-[12px] border border-[#151515] px-[16px] py-[8px] transition-opacity ${
                activeCharacter === '' ? 'cursor-not-allowed opacity-50' : ''
              }`}
              onClick={handleResetCharacter}
              disabled={activeCharacter === ''}
            >
              Сбросить
            </button>
          </div>

          <div className="border-t border-dashed border-[#151515]/50 px-[24px] pb-[24px] pt-[16px]">
            <span className="block shrink-0">
              Найдено препаратов:&nbsp;{totalMedicines}
            </span>
            {/* Прокручивается только список препаратов: поиск, алфавит и
                пагинация остаются на месте. */}
            {medicineList.length > 0 && (
              <div className="mt-[24px] flex max-h-[46vh] w-full flex-col overflow-y-auto bg-white">
                <div className="hidden border-b border-[#151515]/10 py-[12px] font-normal text-[#151515]/40 md:grid md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_100px] md:gap-[16px]">
                  <span>Название</span>
                  <span>Форма выпуска</span>
                  <span className="text-right">Цена</span>
                </div>
                {medicineList.map((medicine) => {
                  const releaseForm = [
                    medicine.form_type?.value,
                    medicine.dosage != null
                      ? `массой ${medicine.dosage} ${
                          medicine.dosage_type?.value ?? ''
                        }`.trim()
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <button
                      key={medicine.id}
                      type="button"
                      onClick={() => handleMedicineSelect(medicine)}
                      className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-x-[12px] gap-y-[4px] border-b border-[#151515]/10 py-[12px] text-left md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_100px] md:gap-[16px]"
                    >
                      <div className="flex min-w-0 items-center gap-[12px] font-bold">
                        {medicine.picture?.image && (
                          <img
                            alt=""
                            height={44}
                            width={44}
                            className="size-[44px] shrink-0 object-contain"
                            src={'https://my-pax.ru/' + medicine.picture.image}
                          />
                        )}
                        <span className="min-w-0 break-words">
                          {medicine.name}
                        </span>
                      </div>
                      <span className="row-start-2 min-w-0 text-sm text-[#151515]/50 md:row-start-auto md:text-base md:text-[#151515]">
                        {releaseForm || '—'}
                      </span>
                      <span className="row-span-2 self-center whitespace-nowrap text-right md:row-span-1">
                        {formatMoney(medicine.medicine_price)}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            <div className="mt-[24px] flex shrink-0 items-center justify-between">
              <div>
                <span>Страница {currentPage}</span>
              </div>
              <div className="flex gap-x-[8px]">
                <button
                  className="rounded-[12px] border border-[#151515] px-[16px] py-[8px] disabled:opacity-40"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Назад
                </button>
                <button
                  className="rounded-[12px] border border-[#151515] px-[16px] py-[8px] disabled:opacity-40"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                >
                  Вперед
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
